import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import { Avatar, AvatarFallback } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Settings, LogOut, ChevronDown, Layers, PanelLeftClose, PanelLeftOpen, BarChart3, MessageSquare, Wallet, Home, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import logoWhite from '../assets/logo-white.svg';

const navItems = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/transactions', label: 'Transactions', icon: Layers },
  { path: '/accounts', label: 'Accounts', icon: Wallet },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/alto-ai', label: 'Alto AI', icon: MessageSquare },
];

export const Layout: React.FC = () => {
  const { logout, profile, backgroundJobStatus, refreshAuth } = useAuth();
  const { isPrivacyMode, togglePrivacyMode } = usePrivacy();
  const navigate = useNavigate();
  const location = useLocation();

  // Frontend-only baseline: set when syncing starts, cleared when syncing finishes.
  const [syncBaselineJobs, setSyncBaselineJobs] = useState<number | null>(null);

  const activeJobs = backgroundJobStatus?.active_job_count ?? 0;
  const syncPercent =
    syncBaselineJobs && syncBaselineJobs > 0
      ? Math.min(100, Math.max(0, Math.round((1 - activeJobs / syncBaselineJobs) * 100)))
      : 0;
  
  // Load collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(isCollapsed));
  }, [isCollapsed]);

  // Start/stop polling for background job status only while active
  useEffect(() => {
    if (!backgroundJobStatus?.is_active) return;
    const interval = setInterval(() => {
      refreshAuth();
    }, 5000);
    return () => clearInterval(interval);
  }, [backgroundJobStatus?.is_active, refreshAuth]);

  // Maintain baseline while a sync run is active.
  useEffect(() => {
    const isActive = !!backgroundJobStatus?.is_active;
    const count = backgroundJobStatus?.active_job_count ?? 0;

    if (!isActive || count <= 0) {
      // Sync is not active (or has finished) => clear baseline until next run.
      if (syncBaselineJobs !== null) setSyncBaselineJobs(null);
      return;
    }

    // Sync just started: capture baseline.
    if (syncBaselineJobs === null) {
      setSyncBaselineJobs(count);
      return;
    }

    // If more jobs get enqueued mid-run, keep baseline as the max so percent doesn't go negative.
    if (count > syncBaselineJobs) {
      setSyncBaselineJobs(count);
    }
  }, [backgroundJobStatus?.is_active, backgroundJobStatus?.active_job_count, syncBaselineJobs]);

  // Kick off a single refresh when app code enqueues jobs (no idle polling)
  useEffect(() => {
    const handler = () => refreshAuth();
    window.addEventListener('alto:jobs_changed', handler);
    return () => window.removeEventListener('alto:jobs_changed', handler);
  }, [refreshAuth]);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = () => {
    if (!profile?.first_name && !profile?.last_name) return 'U';
    const first = profile?.first_name?.[0] || '';
    const last = profile?.last_name?.[0] || '';
    return `${first}${last}`.toUpperCase();
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex h-screen h-dvh bg-background overflow-x-hidden max-w-[100vw]">
      {/* Desktop Left Sidebar - Hidden on mobile */}
      <aside 
        className={`hidden md:flex border-r flex-col transition-all duration-300 ${
          isCollapsed ? 'w-16' : 'w-60'
        }`}
        style={{ 
          backgroundColor: 'var(--theme-primary)',
          color: 'var(--theme-secondary)'
        }}
      >
        {/* User Dropdown */}
        <div className={`p-4 border-b ${isCollapsed ? 'px-2' : ''}`}>
          {isCollapsed ? (
            <div className="flex justify-center">
              <Avatar className="h-8 w-8 cursor-pointer" onClick={() => setIsCollapsed(false)}>
                <AvatarFallback 
                  className="text-xs font-medium bg-white/20 text-white"
                >
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger className="w-full flex items-center space-x-3 p-2 rounded-md transition-colors text-white">
                <Avatar className="h-8 w-8">
                  <AvatarFallback 
                    className="text-xs font-medium bg-white/20 text-white"
                  >
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium">
                    {profile?.first_name} {profile?.last_name}
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 opacity-70" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Desktop Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 flex flex-col">
          <div className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2 rounded-md text-sm font-medium transition-colors text-white ${
                  isActive(item.path)
                    ? 'bg-white bg-opacity-20'
                    : 'opacity-80 hover:opacity-100 hover:bg-white hover:bg-opacity-10'
                }`}
                title={isCollapsed ? item.label : ''}
              >
                <item.icon className="h-4 w-4" />
                {!isCollapsed && <span>{item.label}</span>}
              </Link>
            ))}
          </div>

          {/* Desktop sync indicator (bottom of navbar section) */}
          {backgroundJobStatus?.is_active && (
            <div className={`mt-auto pt-3 ${isCollapsed ? 'flex justify-center' : ''}`}>
              {isCollapsed ? (
                <div
                  className="inline-flex items-center justify-center h-8 w-8 rounded-xl bg-white/20"
                  title="Syncing Transactions"
                  aria-label="Syncing Transactions"
                >
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                </div>
              ) : (
                <div
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-white/20 text-white text-xs font-medium"
                  title="Syncing Transactions"
                  aria-label="Syncing Transactions"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Syncing Transactions</span>
                  <span className="opacity-80">({syncPercent}%)</span>
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Privacy Toggle & Collapse Buttons */}
        <div className={`p-3 border-t border-white/10 space-y-2`}>
          {/* Privacy Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={togglePrivacyMode}
            className={`w-full ${isCollapsed ? 'justify-center px-0' : 'justify-start'} text-white transition-all ${
              isPrivacyMode 
                ? 'bg-white/20 opacity-100' 
                : 'opacity-80 hover:opacity-100 hover:bg-white hover:bg-opacity-10'
            }`}
            title={isPrivacyMode ? 'Show amounts' : 'Hide amounts'}
          >
            {isCollapsed ? (
              isPrivacyMode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />
            ) : (
              <>
                {isPrivacyMode ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                <span>{isPrivacyMode ? 'Show Amounts' : 'Hide Amounts'}</span>
              </>
            )}
          </Button>

          {/* Sidebar Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className={`w-full ${isCollapsed ? 'justify-center px-0' : 'justify-start'} text-white opacity-80 hover:opacity-100 hover:bg-white hover:bg-opacity-10`}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4 mr-2" />
                <span>Collapse</span>
              </>
            )}
          </Button>

          {/* Logo (bottom of sidebar) */}
          <div className={`pt-2 flex ${isCollapsed ? 'justify-center' : 'justify-start'} items-start w-full h-fit`}>
            <div
              className={`rounded-lg bg-transparent text-black px-3 py-2 flex ${
                isCollapsed ? 'justify-center' : 'justify-start'
              } items-start w-full h-fit`}
              title="Alto"
              aria-label="Alto"
            >
              <img
                src={logoWhite}
                alt="Alto"
                className={isCollapsed ? 'h-6 w-auto' : 'h-7 w-auto'}
                draggable={false}
              />
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-0 overflow-x-hidden">
        {/* Mobile Header - Only visible on mobile */}
        <header 
          className="md:hidden flex items-center justify-between px-4 py-3 border-b text-white"
          style={{ 
            backgroundColor: 'var(--theme-primary)'
          }}
        >
          <Link
            to="/"
            className="flex items-center"
            aria-label="Alto"
            title="Alto"
          >
            <img
              src={logoWhite}
              alt="Alto"
              className="h-6 w-auto"
              draggable={false}
            />
          </Link>
          <div className="flex items-center gap-2">
            {backgroundJobStatus?.is_active && (
              <div
                className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-white/20 text-white text-xs font-medium"
                title="Syncing Transactions"
                aria-label="Syncing Transactions"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="hidden sm:inline">Syncing Transactions</span>
                <span className="sm:hidden">Syncing</span>
                <span className="opacity-80">({syncPercent}%)</span>
              </div>
            )}
            {/* Privacy Toggle for Mobile */}
            <Button
              variant="ghost"
              size="sm"
              onClick={togglePrivacyMode}
              className={`p-2 transition-all ${
                isPrivacyMode 
                  ? 'bg-white/20' 
                  : 'hover:bg-white hover:bg-opacity-10'
              }`}
              title={isPrivacyMode ? 'Show amounts' : 'Hide amounts'}
            >
              {isPrivacyMode ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="p-1 hover:bg-white hover:bg-opacity-10">
                <Avatar className="h-8 w-8">
                  <AvatarFallback 
                    className="text-xs font-medium bg-white/20 text-white"
                  >
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-sm font-medium">
                {profile?.first_name} {profile?.last_name}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto pb-16 md:pb-0">
          <Outlet />
        </main>

        {/* Mobile Bottom Navigation - Only visible on mobile */}
        <nav 
          className="md:hidden fixed bottom-0 left-0 right-0 border-t flex items-center justify-around py-2 safe-area-bottom text-white relative"
          style={{ 
            backgroundColor: 'var(--theme-primary)'
          }}
        >
          {/* Mobile bottom-nav sync indicator (right side) */}
          {backgroundJobStatus?.is_active && (
            <div
              className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-2 px-2 py-1 rounded-md bg-white/20 text-white text-xs font-medium"
              title="Syncing Transactions"
              aria-label="Syncing Transactions"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Syncing</span>
              <span className="opacity-80">({syncPercent}%)</span>
            </div>
          )}
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center px-3 py-1.5 min-w-[64px] rounded-lg transition-colors ${
                isActive(item.path)
                  ? 'bg-white bg-opacity-20'
                  : 'opacity-70'
              }`}
            >
              <item.icon className="h-5 w-5" />
              <span className={`text-[10px] mt-1 ${isActive(item.path) ? 'font-medium' : ''}`}>
                {item.label}
              </span>
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
};
